import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ExternalLink, Trash2, RefreshCw, AlertTriangle, ClipboardPaste, ShieldAlert, Bug, CheckCircle2, X } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { useAuth } from "@clerk/react";

interface CfBlockedItem {
  school_no: string;
  school_name: string;
  urls: string[];
}

interface FailSchoolItem {
  key: string;
  school_no?: string;
  school_name: string;
  urls: string[];
  error?: string;
  fail_type?: string;
  hardcoded?: boolean;
}

const DAILY_FAIL_SCHOOLS: Omit<FailSchoolItem, "key">[] = [
  { school_name: "沙田公立學校(小學部)", school_no: "112526000123", urls: ["https://www.shatinpublicschool.edu.hk/download/directLinkFile/202601211453000234569-1395150809.pdf?v=202603231548&nnnid=145"], hardcoded: true },
  { school_name: "通德學校", school_no: "112666000123", urls: ["http://www.ttsch.edu.hk/it-school/php/webcms/public/mainpage/sitemap.php"], hardcoded: true },
  { school_name: "東莞工商總會張煌偉小學", school_no: "115070000123", urls: ["https://drive.google.com/file/d/1cHIWiheBMoMtqhQA9l_ubGIJCi4ASimH/view?usp=sharing"], hardcoded: true },
  { school_name: "海怡寶血小學", school_no: "535591000123", urls: ["https://www.pbpssh.edu.hk/it-school/php/webcms/files/upload/tinymce//Tender/202605_1_1780556758.pdf"], hardcoded: true },
  { school_name: "九龍塘官立小學", school_no: "539180000123", urls: ["https://www.klntong.edu.hk/it-school/php/webcms/public/mainpage/tpl.php?refid=0&component_id=1&lang=zh"], hardcoded: true },
  { school_name: "伊利沙伯中學舊生會小學分校", school_no: "529532000123", urls: ["https://www.qbps.edu.hk/latest_news"], hardcoded: true },
  { school_name: "賽馬會萬鈞毅智書院", school_no: "529788000133", urls: ["https://www.jcmkec.edu.hk/tc/news.php?wid=197&cid=13"], hardcoded: true },
];

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface PageUpdate {
  id: number;
  schoolNo: string;
  schoolName: string | null;
  monitorUrl: string | null;
  detectedAt: string;
  oldContentLength: number | null;
  newContentLength: number | null;
  oldContentHash: string | null;
  newContentHash: string | null;
}

function isToday(dateStr: string): boolean {
  const hktDate = (dt: Date) => dt.toLocaleDateString("en-CA", { timeZone: "Asia/Hong_Kong" });
  return hktDate(new Date(dateStr)) === hktDate(new Date());
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString("zh-HK", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Hong_Kong" });
}

function parseJsonInput(text: string): { items: unknown[] | null; cfBlocked: CfBlockedItem[] } {
  try {
    const parsed = JSON.parse(text.trim());
    const cfBlocked: CfBlockedItem[] = Array.isArray(parsed?.cf_blocked) ? parsed.cf_blocked : [];
    if (Array.isArray(parsed)) return { items: parsed, cfBlocked };
    if (Array.isArray(parsed?.records)) return { items: parsed.records, cfBlocked };
    if (Array.isArray(parsed?.data)) return { items: parsed.data, cfBlocked };
    return { items: [parsed], cfBlocked };
  } catch {
    return { items: null, cfBlocked: [] };
  }
}

interface ParsedFailResult {
  dailyCheckSchools: FailSchoolItem[];
  failedSchools: FailSchoolItem[];
  isNewFormat: boolean;
}

function mapRawSchools(raw: unknown[], prefix: string): FailSchoolItem[] {
  return raw.map((item, i) => {
    const r = item as Record<string, unknown>;
    const name = String(r.school_name ?? r.name ?? r.schoolName ?? "未知學校");
    const rawUrls = r.urls ?? r.url ?? r.tender_url ?? r.pageUrl;
    const urls: string[] = Array.isArray(rawUrls)
      ? rawUrls.map(String)
      : rawUrls ? [String(rawUrls)] : [];
    return {
      key: `${prefix}-${i}-${name}`,
      school_no: r.school_no ? String(r.school_no) : undefined,
      school_name: name,
      urls,
      error: r.error ? String(r.error) : r.reason ? String(r.reason) : r.fail_reason ? String(r.fail_reason) : undefined,
      fail_type: r.fail_type ? String(r.fail_type) : undefined,
    };
  });
}

function parseFailSchoolsJson(text: string): ParsedFailResult | null {
  try {
    const parsed = JSON.parse(text.trim());

    if (Array.isArray(parsed?.daily_check_schools)) {
      const dailyCheckSchools = mapRawSchools(parsed.daily_check_schools, "daily").map(s => ({ ...s, hardcoded: true }));
      const failedSchools = Array.isArray(parsed?.schools) ? mapRawSchools(parsed.schools, "paste") : [];
      return { dailyCheckSchools, failedSchools, isNewFormat: true };
    }

    let raw: unknown[] = [];
    if (Array.isArray(parsed)) raw = parsed;
    else if (Array.isArray(parsed?.failed_schools)) raw = parsed.failed_schools;
    else if (Array.isArray(parsed?.fail_schools)) raw = parsed.fail_schools;
    else if (Array.isArray(parsed?.schools)) raw = parsed.schools;
    else if (Array.isArray(parsed?.failed)) raw = parsed.failed;
    else return null;

    return { dailyCheckSchools: [], failedSchools: mapRawSchools(raw, "paste"), isNewFormat: false };
  } catch {
    return null;
  }
}

export default function AdminSchoolsUpdatePage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [importedCfBlocked, setImportedCfBlocked] = useState<CfBlockedItem[]>([]);

  const [failPasteOpen, setFailPasteOpen] = useState(false);
  const [failPasteText, setFailPasteText] = useState("");
  const [failPasteError, setFailPasteError] = useState<string | null>(null);
  const [failSchools, setFailSchools] = useState<FailSchoolItem[]>(() =>
    DAILY_FAIL_SCHOOLS.map((s, i) => ({ ...s, key: `hardcoded-${i}` }))
  );
  const [reviewedKeys, setReviewedKeys] = useState<Set<string>>(new Set());
  const [showFailPanel, setShowFailPanel] = useState(false);

  const { data: rows = [], isLoading, refetch } = useQuery<PageUpdate[]>({
    queryKey: ["admin-schools-update"],
    queryFn: async () => {
      const token = await getToken();
      const r = await fetch(`${BASE}/api/admin/schools-update`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Failed to load");
      return r.json();
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: number) => {
      const token = await getToken();
      await fetch(`${BASE}/api/admin/schools-update/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-schools-update"] }),
  });

  const deleteAllMut = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      await fetch(`${BASE}/api/admin/schools-update`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-schools-update"] });
      setDeleteAllOpen(false);
    },
  });

  const importMut = useMutation({
    mutationFn: async (items: unknown[]) => {
      const token = await getToken();
      const r = await fetch(`${BASE}/api/admin/schools-update/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(items),
      });
      if (!r.ok) throw new Error("Import failed");
      return r.json();
    },
    onError: () => {
      setPasteError("匯入失敗，請確認 JSON 格式正確");
    },
  });

  function handlePasteImport() {
    setPasteError(null);
    const { items, cfBlocked } = parseJsonInput(pasteText);
    if (!items) {
      setPasteError("JSON 格式錯誤，請重新確認");
      return;
    }
    if (items.length === 0 && cfBlocked.length === 0) {
      setPasteError("沒有找到任何記錄");
      return;
    }
    importMut.mutate(items, { onSuccess: (data: any) => {
      setImportResult(`成功匯入 ${data.inserted} 筆記錄`);
      setImportedCfBlocked(cfBlocked);
      qc.invalidateQueries({ queryKey: ["admin-schools-update"] });
      setPasteOpen(false);
      setPasteText("");
      setPasteError(null);
    }});
  }

  function handleFailPasteImport() {
    setFailPasteError(null);
    const result = parseFailSchoolsJson(failPasteText);
    if (!result) {
      setFailPasteError("JSON 格式錯誤：請確認含有學校列表");
      return;
    }
    let dailySchools: FailSchoolItem[];
    if (result.isNewFormat && result.dailyCheckSchools.length > 0) {
      dailySchools = result.dailyCheckSchools;
    } else {
      dailySchools = DAILY_FAIL_SCHOOLS.map((s, i) => ({ ...s, key: `hardcoded-${i}` }));
    }
    const deduped = result.failedSchools.filter(s =>
      !dailySchools.some(h => h.school_name === s.school_name)
    );
    setFailSchools([...dailySchools, ...deduped]);
    setReviewedKeys(new Set());
    setShowFailPanel(true);
    setFailPasteOpen(false);
    setFailPasteText("");
  }

  function markReviewed(key: string) {
    setReviewedKeys(prev => new Set([...prev, key]));
  }

  function markAllReviewed() {
    setReviewedKeys(new Set(failSchools.map(s => s.key)));
  }

  const pendingFail = failSchools.filter(s => !reviewedKeys.has(s.key));
  const todayCount = rows.filter(r => isToday(r.detectedAt)).length;

  return (
    <AdminLayout tab="schools-update">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">網頁更新記錄</h2>
            <p className="text-sm text-muted-foreground">
              共 {rows.length} 筆記錄
              {todayCount > 0 && (
                <span className="ml-2 text-amber-600 font-medium">· 今日更新 {todayCount} 筆</span>
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5">
              <RefreshCw className="h-4 w-4" /> 刷新
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => { setPasteOpen(true); setPasteText(""); setPasteError(null); }}
            >
              <ClipboardPaste className="h-4 w-4" /> 貼上 JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              className={`gap-1.5 ${showFailPanel ? "bg-red-50 border-red-300 text-red-700 hover:bg-red-100" : ""}`}
              onClick={() => {
                if (showFailPanel) { setShowFailPanel(false); } else { setFailPasteOpen(true); }
              }}
            >
              <Bug className="h-4 w-4" />
              爬取失敗 {pendingFail.length > 0 && <Badge className="ml-0.5 h-4 px-1.5 text-[10px] bg-red-500 text-white">{pendingFail.length}</Badge>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={() => setDeleteAllOpen(true)}
            >
              <Trash2 className="h-4 w-4" /> 清除全部
            </Button>
          </div>
        </div>

        {importResult && (
          <div className="rounded-md border bg-green-50 border-green-200">
            <div className="flex items-center justify-between px-4 py-2">
              <span className="text-sm text-green-700">{importResult}</span>
              <button onClick={() => { setImportResult(null); setImportedCfBlocked([]); }} className="text-xs text-green-600 underline">關閉</button>
            </div>
            {importedCfBlocked.length > 0 && (
              <div className="border-t border-green-200 px-4 py-2.5">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-2">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Cloudflare 封鎖（{importedCfBlocked.length} 間）— 未能監察
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {importedCfBlocked.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      <ShieldAlert className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-amber-800 truncate">{s.school_name}</div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {s.urls.map((url, ui) => (
                            <a key={ui} href={url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 hover:text-amber-800 underline underline-offset-1 truncate max-w-[180px]"
                              title={url}>
                              <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                              {new URL(url).hostname}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── 爬取失敗學校審查面板 ── */}
        {showFailPanel && (
          <div className="rounded-lg border border-red-200 bg-red-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-red-100 border-b border-red-200">
              <div className="flex items-center gap-2">
                <Bug className="h-4 w-4 text-red-600" />
                <span className="text-sm font-semibold text-red-800">
                  爬取失敗學校審查
                </span>
                <Badge className="bg-red-500 text-white text-[10px] px-1.5 h-4">
                  待審 {pendingFail.length} / {failSchools.length}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFailPasteOpen(true)}
                  className="text-xs text-red-600 underline hover:text-red-800"
                >
                  貼上新 JSON
                </button>
                <button
                  onClick={markAllReviewed}
                  className="text-xs text-red-600 underline hover:text-red-800"
                >
                  全部已查
                </button>
                <button onClick={() => setShowFailPanel(false)} className="text-red-400 hover:text-red-700 ml-1">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-red-100/60 text-xs text-red-700">
                    <th className="text-left px-3 py-2 font-semibold w-[180px]">學校名稱</th>
                    <th className="text-left px-3 py-2 font-semibold w-[120px]">學校編號</th>
                    <th className="text-left px-3 py-2 font-semibold">連結 / 錯誤</th>
                    <th className="text-left px-3 py-2 font-semibold w-[80px]">類型</th>
                    <th className="px-3 py-2 w-[70px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {failSchools.map(school => {
                    const done = reviewedKeys.has(school.key);
                    return (
                      <tr
                        key={school.key}
                        className={`border-t border-red-100 transition-colors ${done ? "opacity-40 bg-white" : "bg-white hover:bg-red-50"}`}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            {school.hardcoded && (
                              <Badge className="text-[9px] px-1 h-3.5 bg-orange-400 text-white shrink-0">每日</Badge>
                            )}
                            <span className="font-medium text-xs">{school.school_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                          {school.school_no || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1 items-center">
                            {school.urls.length > 0 ? school.urls.map((url, ui) => (
                              <a key={ui} href={url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-0.5 text-[11px] text-blue-600 hover:text-blue-800 underline underline-offset-1 max-w-[260px] truncate"
                                title={url}>
                                <ExternalLink className="h-3 w-3 shrink-0" />
                                {(() => { try { return new URL(url).hostname; } catch { return url; } })()}
                              </a>
                            )) : (
                              <span className="text-[11px] text-muted-foreground italic">無 URL</span>
                            )}
                            {school.error && (
                              <span className="text-[11px] text-red-500 ml-1" title={school.error}>
                                · {school.error.length > 40 ? school.error.slice(0, 40) + "…" : school.error}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {school.fail_type ? (
                            <Badge variant="outline" className="text-[10px] px-1.5 h-4 border-red-300 text-red-600">
                              {school.fail_type}
                            </Badge>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {done ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <button
                              onClick={() => markReviewed(school.key)}
                              className="inline-flex items-center gap-1 text-[11px] text-green-600 hover:text-green-800 font-medium"
                              title="標記為已查"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> 已查
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pendingFail.length === 0 && (
              <div className="text-center py-2 text-xs text-green-600 bg-green-50 border-t border-green-100">
                ✅ 全部學校已審查完畢
              </div>
            )}
          </div>
        )}

        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">學校編號</TableHead>
                <TableHead>學校名稱</TableHead>
                <TableHead className="w-[140px]">偵測時間</TableHead>
                <TableHead className="w-[130px]">內容變化</TableHead>
                <TableHead className="w-[100px] text-center">連結</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">載入中...</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">暫無記錄，請貼上 JSON 數據匯入</TableCell>
                </TableRow>
              ) : rows.map(row => {
                const today = isToday(row.detectedAt);
                return (
                  <TableRow
                    key={row.id}
                    className={today ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-muted/40"}
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">{row.schoolNo || "—"}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {today && <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0 h-4 shrink-0">今日</Badge>}
                        {row.schoolName ?? <span className="text-muted-foreground italic">未知</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(row.detectedAt)}</TableCell>
                    <TableCell className="text-xs">
                      {(row.oldContentLength != null && row.newContentLength != null) ? (
                        <span className={row.newContentLength > row.oldContentLength ? "text-green-600" : row.newContentLength < row.oldContentLength ? "text-red-600" : "text-muted-foreground"}>
                          {row.oldContentLength} → {row.newContentLength}
                          {row.newContentLength !== row.oldContentLength && (
                            <span className="ml-1 font-medium">
                              ({row.newContentLength > row.oldContentLength ? "+" : ""}{row.newContentLength - row.oldContentLength})
                            </span>
                          )}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-center">
                      {row.monitorUrl ? (
                        <a
                          href={row.monitorUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline text-xs font-medium"
                        >
                          開啟 <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => deleteMut.mutate(row.id)}
                        disabled={deleteMut.isPending}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        title="刪除"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {rows.length > 0 && (
          <p className="text-xs text-muted-foreground text-right">
            排序：最新優先 · 今日更新以黃色底顯示
          </p>
        )}
      </div>

      {/* 貼上 JSON Dialog */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-2xl flex flex-col max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5" /> 貼上 JSON 數據
            </DialogTitle>
            <DialogDescription>
              支援格式：純陣列 <code className="text-xs bg-muted px-1 rounded">[ ... ]</code>，或含 <code className="text-xs bg-muted px-1 rounded">records</code> 欄位的物件
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 space-y-3 pr-1">
            <textarea
              className="w-full h-64 font-mono text-xs border rounded-md p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary bg-muted/30"
              placeholder='貼上 JSON，例如：&#10;{"exported_at":"...","records":[{"school_no":"...","school_name":"..."}]}'
              value={pasteText}
              onChange={e => { setPasteText(e.target.value); setPasteError(null); }}
              spellCheck={false}
            />
            {pasteError && (
              <p className="text-sm text-destructive">{pasteError}</p>
            )}
            {pasteText.trim() && !pasteError && (() => {
              const { items, cfBlocked } = parseJsonInput(pasteText);
              if (!items) return null;
              return (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    偵測到 <span className="font-medium text-foreground">{items.length}</span> 筆更新記錄
                    {cfBlocked.length > 0 && (
                      <span className="ml-2 text-amber-600 font-medium">· {cfBlocked.length} 間被 Cloudflare 封鎖</span>
                    )}
                  </p>
                  {cfBlocked.length > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-2">
                        <ShieldAlert className="h-3.5 w-3.5" />
                        Cloudflare 封鎖學校（不會被匯入，僅供參考）
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {cfBlocked.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 bg-white border border-amber-200 rounded px-2 py-1.5">
                            <ShieldAlert className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-amber-800">{s.school_name}</div>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {s.urls.map((url, ui) => (
                                  <a key={ui} href={url} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 hover:text-amber-800 underline underline-offset-1"
                                    title={url}>
                                    <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                                    {new URL(url).hostname}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteOpen(false)}>取消</Button>
            <Button
              onClick={handlePasteImport}
              disabled={!pasteText.trim() || importMut.isPending}
            >
              {importMut.isPending ? "匯入中..." : "確認匯入"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 爬取失敗 JSON Dialog */}
      <Dialog open={failPasteOpen} onOpenChange={setFailPasteOpen}>
        <DialogContent className="max-w-2xl flex flex-col max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-500" /> 貼上 scrape_fail_schools.json
            </DialogTitle>
            <DialogDescription>
              支援新格式（含 <code className="text-xs bg-muted px-1 rounded">daily_check_schools</code> + <code className="text-xs bg-muted px-1 rounded">schools</code>）或舊格式（<code className="text-xs bg-muted px-1 rounded">failed_schools</code> 陣列）。新格式會自動從 JSON 讀取每日檢查名單；舊格式則預載 {DAILY_FAIL_SCHOOLS.length} 間固定每日學校。
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 space-y-3 pr-1">
            <textarea
              className="w-full h-56 font-mono text-xs border rounded-md p-3 resize-none focus:outline-none focus:ring-2 focus:ring-red-400 bg-muted/30"
              placeholder='貼上 scrape_fail_schools.json 內容，例如：&#10;{"failed_schools":[{"school_name":"...","url":"...","error":"..."}]}'
              value={failPasteText}
              onChange={e => { setFailPasteText(e.target.value); setFailPasteError(null); }}
              spellCheck={false}
            />
            {failPasteError && (
              <p className="text-sm text-destructive">{failPasteError}</p>
            )}
            {failPasteText.trim() && !failPasteError && (() => {
              const result = parseFailSchoolsJson(failPasteText);
              if (!result) return null;
              if (result.isNewFormat) {
                return (
                  <div className="flex gap-3 text-sm">
                    <span className="text-orange-600 font-medium">每日檢查：{result.dailyCheckSchools.length} 間</span>
                    <span className="text-muted-foreground">|</span>
                    <span className="text-red-600 font-medium">爬取失敗：{result.failedSchools.length} 間</span>
                  </div>
                );
              }
              return (
                <p className="text-sm text-muted-foreground">
                  偵測到 <span className="font-medium text-foreground">{result.failedSchools.length}</span> 間失敗學校
                  <span className="ml-2 text-orange-600">+ {DAILY_FAIL_SCHOOLS.length} 間每日學校</span>
                </p>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFailPasteOpen(false); setShowFailPanel(true); }}>
              只顯示每日學校
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleFailPasteImport}
              disabled={!failPasteText.trim()}
            >
              載入並審查
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 清除全部 Dialog */}
      <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> 確認清除全部記錄
            </DialogTitle>
            <DialogDescription>
              此操作將刪除全部 {rows.length} 筆網頁更新記錄，無法復原。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllOpen(false)}>取消</Button>
            <Button
              variant="destructive"
              onClick={() => deleteAllMut.mutate()}
              disabled={deleteAllMut.isPending}
            >
              {deleteAllMut.isPending ? "刪除中..." : "確認刪除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
